import { useQuery } from "@tanstack/react-query";
import { addDays, endOfMonth, format, startOfMonth, startOfWeek } from "date-fns";
import { useParams } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { tryParse } from "@/helpers/date";
import { useProject } from "@/hooks/Network/useProject";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParam } from "@/hooks/useQueryParam";
import { getBookingsExportFilename } from "@/modules/bookings/helpers";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";

import { AssetDetailContent, type AssetDetailContentProps } from "./AssetDetailContent";

export function AssetDetailPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const { name: projectName } = useProject();
  const { id: assetId } = useParams<{ id: string }>();

  const bookableAssetQueries = useBookableAssetsQueries();
  const {
    data: assetDetails,
    isLoading: isLoadingAssetDetails,
    error: errorLoadingAssetDetails,
  } = useQuery({
    ...bookableAssetQueries.details({
      assetId: assetId!,
    }),
    enabled: !!assetId,
  });

  const [queryParamView, setView] = useQueryParam("view");
  const view = queryParamView === "month" ? "month" : "week";

  const [queryParamDate, setSelectedStartDate] = useQueryParam("date");
  const queryStartDate = queryParamDate ? tryParse("dd-MM-yyyy", queryParamDate) : undefined;
  const selectedStartDate =
    view === "month"
      ? startOfMonth(queryStartDate || clockDate)
      : startOfWeek(queryStartDate || clockDate, { weekStartsOn: 1 });
  const selectedEndDate = view === "week" ? addDays(selectedStartDate, 6) : endOfMonth(selectedStartDate);

  function onChangeView(view: "week" | "month", startDate: Date) {
    setView(view);
    setSelectedStartDate(format(startDate, "dd-MM-yyyy"));
  }

  const bookingsQuery = {
    Date: format(selectedStartDate, "yyyy-MM-dd"),
    Until: format(selectedEndDate, "yyyy-MM-dd"),
  };
  const {
    data: bookings = [],
    isLoading: isLoadingBookings,
    error: errorLoadingBookings,
  } = useQuery({
    ...bookableAssetQueries.assetBookings({ assetId: assetId!, query: bookingsQuery }),
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: assetStatistics,
    isFetching: isFetchingAssetStatistics,
    error: assetStatisticsError,
  } = useQuery({
    ...bookableAssetQueries.statistics({ assetId: assetId! }),
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const exportBookingsMutation = bookableAssetMutations.useExportAssetBookings(assetId!);

  const error = errorLoadingAssetDetails || errorLoadingBookings || assetStatisticsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const loading = isLoadingAssetDetails;
  if (loading) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: AssetDetailContentProps = {
    assetDetails: assetDetails!,
    isLoadingBookings,
    bookings,
    isLoadingAssetStatistics: isFetchingAssetStatistics,
    assetStatistics,
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    view,
    onChangeView,
    exportBookings: (includeHistory: boolean) =>
      exportBookingsMutation.mutateAsync({
        includeHistory,
        filename: getBookingsExportFilename(projectName, assetDetails!.name),
      }),
    isExporting: exportBookingsMutation.isPending,
  };

  return <AssetDetailContent {...contentProps} />;
}
